import type { EnumProductType } from '@/types/enum-product-type'

/**
 * Query param que carrega o tipo de conteúdo ativo quando o usuário navega
 * por uma área "real" (ex.: /cursos/oficinas-mes-do-professor?tipo=webinar).
 *
 * As rotas de tipo (/cursos/webinars, /cursos/trilhas-de-aprendizagem e
 * /cursos/pos-graduacoes) já carregam o tipo no próprio slug.
 */
export const PRODUCT_TYPE_QUERY_KEY = 'tipo'

export const FILTERABLE_PRODUCT_TYPES = [
  'course',
  'postgraduate',
  'track',
  'webinar',
] as const satisfies readonly EnumProductType[]

export type FilterableProductType = (typeof FILTERABLE_PRODUCT_TYPES)[number]

export const DEFAULT_PRODUCT_TYPE: FilterableProductType = 'course'

const PRODUCT_TYPE_BY_SLUG: Record<string, FilterableProductType> = {
  'pos-graduacoes': 'postgraduate',
  'trilhas-de-aprendizagem': 'track',
  webinars: 'webinar',
}

const SLUG_BY_PRODUCT_TYPE: Record<FilterableProductType, string> = {
  course: '',
  postgraduate: 'pos-graduacoes',
  track: 'trilhas-de-aprendizagem',
  webinar: 'webinars',
}

export function isFilterableProductType(
  value: unknown
): value is FilterableProductType {
  return (
    typeof value === 'string' &&
    (FILTERABLE_PRODUCT_TYPES as readonly string[]).includes(value)
  )
}

/** Retorna o tipo quando o slug é uma rota de tipo; caso contrário, null. */
export function getProductTypeFromSlug(
  slug: string | undefined | null
): FilterableProductType | null {
  if (!slug) {
    return null
  }
  return PRODUCT_TYPE_BY_SLUG[slug] ?? null
}

/** Indica se o slug representa um tipo de conteúdo e não uma área real. */
export function isProductTypeSlug(slug: string | undefined | null): boolean {
  return getProductTypeFromSlug(slug) !== null
}

/**
 * Resolve o tipo de conteúdo ativo:
 * 1. slug de tipo (webinars, trilhas-de-aprendizagem, pos-graduacoes);
 * 2. query param `tipo` válido;
 * 3. Curso Online.
 */
export function resolveProductType({
  slug,
  type,
}: {
  slug?: string | null
  type?: string | null
}): FilterableProductType {
  return (
    getProductTypeFromSlug(slug) ??
    (isFilterableProductType(type) ? type : DEFAULT_PRODUCT_TYPE)
  )
}

/** Caminho base da listagem de um tipo de conteúdo (sem área). */
export function getProductTypeBasePath(type: string | undefined | null) {
  const slug = isFilterableProductType(type) ? SLUG_BY_PRODUCT_TYPE[type] : ''
  return slug ? `/cursos/${slug}` : '/cursos'
}

/**
 * Caminho ao remover a área selecionada: volta para a listagem do tipo ativo.
 * Se a "área" era o próprio tipo (ex.: trilhas), volta para a listagem padrão.
 */
export function getPathWithoutArea({
  areaUrl,
  type,
}: {
  areaUrl?: string | null
  type?: string | null
}): string {
  return isProductTypeSlug(areaUrl) ? '/cursos' : getProductTypeBasePath(type)
}

/**
 * Valor do query param `tipo` a ser propagado ao navegar para uma área.
 * Vazio quando o tipo é o padrão ou quando o próprio slug já define o tipo.
 */
export function getProductTypeQueryValue({
  slug,
  type,
}: {
  slug?: string | null
  type?: string | null
}): string {
  if (isProductTypeSlug(slug)) {
    return ''
  }
  if (!isFilterableProductType(type) || type === DEFAULT_PRODUCT_TYPE) {
    return ''
  }
  return type
}
